import { useState, useEffect } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { InstanceFooter } from '../components/layout/InstanceFooter';
import {
  InformationCircleIcon,
  HomeIcon,
  BellIcon,
  Squares2X2Icon,
  FunnelIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

type TocItem = { id: string; label: string; indent?: boolean; icon?: React.ComponentType<{ className?: string }> };

const TOC: TocItem[] = [
  { id: 'about', label: 'About the App', icon: InformationCircleIcon },
  { id: 'home', label: 'Home', icon: HomeIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'views', label: 'Views', icon: Squares2X2Icon },
  { id: 'views-timeline', label: 'Timeline View', indent: true },
  { id: 'views-list', label: 'List View', indent: true },
  { id: 'views-kanban', label: 'Kanban View', indent: true },
  { id: 'views-calendar', label: 'Calendar View', indent: true },
  { id: 'views-custom', label: 'Custom Views', indent: true },
  { id: 'filters', label: 'Filters', icon: FunnelIcon },
  { id: 'filters-default-view', label: 'My Default View', indent: true },
  { id: 'filters-starred-view', label: 'Starred Initiative View', indent: true },
  { id: 'filters-saved-views', label: 'Saved & Shared Views', indent: true },
  { id: 'initiatives', label: 'Initiative Details', icon: DocumentTextIcon },
  { id: 'initiatives-main', label: 'Main Tab', indent: true },
  { id: 'initiatives-dependencies', label: 'Dependencies Tab', indent: true },
  { id: 'initiatives-resources', label: 'Resources Tab', indent: true },
  { id: 'initiatives-scoring', label: 'Scoring Tab', indent: true },
  { id: 'initiatives-intelligence', label: 'Intelligence Tab', indent: true },
  { id: 'initiatives-comments', label: 'Comments Tab', indent: true },
  { id: 'initiatives-auditlog', label: 'Audit Log Tab', indent: true },
  { id: 'initiatives-sharing', label: 'Sharing', indent: true },
  { id: 'initiatives-starred', label: 'Starred', indent: true },
  { id: 'reports', label: 'Reports', icon: ClipboardDocumentListIcon },
  { id: 'reports-audit', label: 'Audit Log', indent: true },
  { id: 'reports-comments', label: 'Comments Log', indent: true },
  { id: 'reports-dependency', label: 'Dependency Log', indent: true },
  { id: 'reports-scoring', label: 'Scoring', indent: true },
  { id: 'batch-update', label: 'Batch Update', icon: PencilSquareIcon },
  { id: 'intelligence', label: 'Intelligence with AI', icon: SparklesIcon },
  { id: 'billing', label: 'Credits & Referrals', icon: CurrencyDollarIcon },
  { id: 'settings', label: 'Settings', icon: Cog6ToothIcon },
  { id: 'settings-organization', label: 'Organization', indent: true },
  { id: 'settings-hierarchy', label: 'Hierarchy', indent: true },
  { id: 'settings-fields', label: 'Initiative Fields', indent: true },
  { id: 'settings-classifications', label: 'Classifications', indent: true },
  { id: 'settings-status', label: 'Status', indent: true },
  { id: 'settings-tags', label: 'Tags', indent: true },
  { id: 'settings-users', label: 'Users & Roles', indent: true },
  { id: 'settings-teams', label: 'Teams & Groups', indent: true },
  { id: 'settings-timeframes', label: 'Timeframes', indent: true },
  { id: 'settings-labs', label: 'Labs', indent: true },
  { id: 'settings-ai-provider', label: 'AI Provider', indent: true },
  { id: 'settings-api-keys', label: 'API Keys', indent: true },
];

function Section({ id, title, icon: Icon, children }: { id: string; title: string; icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2.5">
        {Icon && <Icon className="w-6 h-6 text-blue-600 flex-shrink-0" />}
        {title}
      </h2>
      <div className="text-sm text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-24">
      <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="text-sm text-gray-700 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export function HelpPage() {
  const [activeSection, setActiveSection] = useState('about');

  // Track which section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    for (const item of TOC) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const handleTocClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />

      <main className="flex-1 overflow-y-auto">
        <div className="flex max-w-6xl mx-auto">
          {/* Sidebar TOC - sticky and independently scrollable */}
          <nav className="w-56 flex-shrink-0 sticky top-0 self-start py-6 px-4 max-h-[calc(100vh-64px)] overflow-y-auto">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Contents
            </p>
            <ul className="space-y-0.5">
              {TOC.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleTocClick(item.id)}
                      className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                        item.indent ? 'pl-5' : ''
                      } ${
                        activeSection === item.id
                          ? 'text-blue-600 bg-blue-50 font-medium'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {Icon && <Icon className="w-4 h-4 flex-shrink-0 text-blue-600" />}
                      <span className="truncate">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Content */}
          <div className="flex-1 py-8 px-10">
            <div className="max-w-2xl space-y-10">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Help & Guide</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Everything you need to know about using the Roadmap app.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Contact Us —{' '}
                  <a href="mailto:support@tabillo.com" className="text-indigo-600 hover:text-indigo-800">
                    support@tabillo.com
                  </a>
                </p>
              </div>

              {/* About the App */}
              <Section id="about" title="About the App" icon={InformationCircleIcon}>
                <p>
                  This application is a planning tool for cross-functional teams to manage
                  initiatives grouped by product and sub-product. It provides a centralized view of
                  your organization's roadmap across multiple product divisions.
                </p>
                <p>
                  Teams can plan roadmaps across flexible timeframes, track dependencies between
                  initiatives, manage progress through customizable statuses, prioritize with
                  Impact/Effort scoring and voting, and collaborate with comments, @mentions, and
                  audit trails. The Product/Sub-Product/Initiative hierarchy — and every field's
                  label, visibility, and required status — is configurable by Admins to match your
                  organization's language.
                </p>
              </Section>

              {/* Home */}
              <Section id="home" title="Home" icon={HomeIcon}>
                <p>
                  The homepage is your dashboard, providing a bird's-eye view of all initiatives.
                  It contains several widgets that summarize the current state of your roadmap.
                </p>
                <p>
                  <strong>Stats Overview</strong> shows the total count of initiatives along with a
                  breakdown by status displayed as a stacked bar chart, and a breakdown by
                  classification shown as a donut chart.
                </p>
                <p>
                  <strong>Recent Activity</strong> shows the latest changes made across all
                  initiatives, while <strong>Recent Comments</strong> displays the most recent
                  discussion activity. Dashboard filters let you narrow these widgets by quarter,
                  product, sub-product, or status.
                </p>
                <p>
                  Charts, Recent Activity, and Recent Comments can each be toggled on or off by
                  Admins via Settings → Labs.
                </p>
              </Section>

              {/* Notifications */}
              <Section id="notifications" title="Notifications" icon={BellIcon}>
                <p>
                  When enabled by an Admin (Settings → Labs), the bell icon in the top navigation
                  bar shows a red badge when you have unread notifications. Click it for a preview,
                  or open the full Notifications page.
                </p>
                <p>You receive notifications when:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Someone @mentions you in a comment (requires @Mentions to also be enabled)</li>
                  <li>An initiative is assigned to you as owner</li>
                  <li>An initiative you own is updated by someone else</li>
                </ul>
                <p>
                  The Notifications page has tabs for <strong>All</strong>,{' '}
                  <strong>Mentions</strong>, and <strong>Assigned</strong>. Unread notifications are
                  marked with a blue dot and can be marked read individually or all at once.
                </p>
                <p>
                  <strong>Email notifications:</strong> each person can turn email delivery on or
                  off for themselves; this setting is personal and saves immediately.
                </p>
              </Section>

              {/* Views */}
              <Section id="views" title="Views" icon={Squares2X2Icon}>
                <p>
                  The app provides several views for visualizing your roadmap — Timeline, List,
                  Kanban, and Calendar. They share the same filter bar, letting you narrow by
                  quarter, product, sub-product, status, owner, classification, tags, and
                  dependencies. Timeline and List can be exported as PNG, PDF, or PowerPoint. Kanban
                  and Calendar are optional and can be turned on or off by an Admin in Settings →
                  Labs → views.
                </p>
              </Section>

              <SubSection id="views-timeline" title="Timeline View">
                <p>
                  A Gantt-style view that plots initiatives on a quarterly grid. Each initiative
                  appears as a horizontal bar spanning its start and end quarters, color-coded by
                  classification. Starred initiatives show a small amber ★ on their bar. Initiatives
                  are grouped under their product and sub-product. A Weekly mode plots date-scheduled
                  initiatives on an exact-date grid instead.
                </p>
                <p>
                  Click any initiative bar to view its details. Editors and Admins can add new
                  initiatives using the "Add Initiative" button in the top navigation bar, and edit
                  or delete existing ones from the detail panel. A "Present" button (or ⌘⇧P) opens a
                  full-screen presentation mode for walking through the roadmap in a meeting.
                </p>
              </SubSection>

              <SubSection id="views-list" title="List View">
                <p>
                  A table-based view showing initiatives grouped by product and sub-product. Each
                  group row displays a pill badge with the count of initiatives inside it. Columns
                  include initiative name, status, start/end quarter, and tags. Starred initiatives
                  show a small amber ★ in the name column.
                </p>
                <p>
                  Click any row to open its details. The same add, edit, and delete actions are
                  available here as in the Timeline view.
                </p>
              </SubSection>

              <SubSection id="views-kanban" title="Kanban View">
                <p>
                  An optional board (Settings → Labs) that arranges initiatives into columns by
                  status, grouped into product / sub-product swimlanes down the left. Editors and
                  Admins can drag a card to a different column to change its status — the change
                  saves immediately and reverts if the save fails. Viewers can see the board but
                  cannot drag.
                </p>
              </SubSection>

              <SubSection id="views-calendar" title="Calendar View">
                <p>
                  An optional calendar (Settings → Labs) for date-driven work, with Month, Week, and
                  Agenda layouts. Initiatives can be scheduled either by month (the default start/end
                  quarter) or by an exact start/end date set in the initiative modal — the Calendar
                  places each initiative on its exact dates when available.
                </p>
              </SubSection>

              <SubSection id="views-custom" title="Custom Views">
                <p>
                  A <strong>Custom View</strong> is a named set of filters saved on the Timeline,
                  List, Kanban, or Calendar view. Editors and Admins create them; they can be kept
                  private or shared with Groups, Teams, or specific Users. This is available only
                  when an Admin enables <strong>Saved Views</strong> in Settings → Labs. (Full
                  details are in <strong>Filters → Saved &amp; Shared Views</strong>.)
                </p>
                <p>
                  <strong>Switching views.</strong> When you have access to at least one custom
                  view, a caret (▾) appears next to <strong>Timeline</strong>,{' '}
                  <strong>List</strong>, <strong>Kanban</strong>, and <strong>Calendar</strong> in
                  the top nav. Open it to switch between the default view and any custom view; the
                  name of the current custom view shows in small grey text under the label.
                </p>
              </SubSection>

              {/* Filters */}
              <Section id="filters" title="Filters" icon={FunnelIcon}>
                <p>
                  The filter bar appears at the top of every view. It lets you narrow the roadmap by
                  quarter, product, sub-product, status, owner, classification, tags, starred
                  status, and whether an initiative has dependencies (plus Impact/Effort/Votes on
                  the Scoring page). Active filters appear as chips; click × on any chip to remove
                  it.
                </p>
              </Section>

              <SubSection id="filters-default-view" title="My Default View">
                <p>
                  Your <strong>default view</strong> is what loads automatically when you open a
                  view. Apply filters, then click the bookmark icon to save them as your personal
                  default — filled means saved; click again to clear it and return to the standard
                  view. This works on every view that has the bookmark icon, including report pages
                  like Audit Log and Dependency Log.
                </p>
                <p>
                  When the <strong>Saved Views</strong> Labs feature is enabled, the bookmark icon
                  opens a fuller menu — see <strong>Saved &amp; Shared Views</strong> below.
                </p>
              </SubSection>

              <SubSection id="filters-starred-view" title="Starred Initiative View">
                <p>
                  <strong>Starring</strong> marks an initiative as strategically important. The star
                  icon appears in the initiative detail header — gray outline means not starred,
                  amber filled means starred. Only the initiative's <strong>owner</strong> or an{' '}
                  <strong>Admin</strong> can star or unstar it; everyone else sees it as a read-only
                  indicator. A small ★ also appears in the List view name column and on Timeline/
                  Kanban/Calendar cards.
                </p>
                <p>
                  <strong>Filtering to starred only:</strong> open the <strong>More</strong> dropdown
                  in the filter bar and check <strong>Starred only</strong>. A chip appears
                  confirming the filter is active.
                </p>
              </SubSection>

              <SubSection id="filters-saved-views" title="Saved & Shared Views">
                <p>
                  <strong>Saved Views</strong> let Editors and Admins capture a set of filters on
                  the Timeline, List, Kanban, or Calendar view under a name and share it with
                  colleagues. This feature is available only when an Admin enables{' '}
                  <strong>Saved Views</strong> in Settings → Labs — until then, the bookmark icon
                  works as a simple personal default (see <strong>My Default View</strong>).
                </p>
                <ul className="space-y-2 ml-1 mt-1">
                  <li className="flex items-start gap-3">
                    <span className="text-lg leading-tight text-gray-300 flex-shrink-0">🔖</span>
                    <span>
                      <strong>Save a view.</strong> Apply any filters, then click the bookmark icon.
                      A two-step wizard asks for a <strong>title</strong>, then who to{' '}
                      <strong>share with</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-lg leading-tight text-blue-600 flex-shrink-0">🔖</span>
                    <span>
                      <strong>Share with.</strong> Choose <strong>Only me</strong> (private),{' '}
                      <strong>Everyone</strong>, <strong>Groups</strong>, <strong>Teams</strong>, or{' '}
                      <strong>Users</strong>. People you share with see the view but cannot change
                      it.
                    </span>
                  </li>
                </ul>
                <p className="mt-2">
                  <strong>Switching views.</strong> A caret (▾) next to Timeline/List/Kanban/
                  Calendar opens a dropdown listing the default view plus any custom view you can
                  access, grouped into "My Views" and "Shared with me" once you have more than a
                  handful.
                </p>
                <p>
                  <strong>Managing a view.</strong> While inside a saved view, the bookmark icon
                  opens an action menu. <strong>Favorite View</strong> (any role) pins it as your
                  personal auto-loading default — favoriting is personal and never affects anyone
                  else. If you own the view (or you're an Admin) you also get{' '}
                  <strong>Update</strong> (save current filter changes back to the view),{' '}
                  <strong>Edit</strong> (rename / change sharing), and <strong>Delete</strong>;
                  Editors and Admins additionally get <strong>Save As..</strong> to branch a new
                  view from the current filters.
                </p>
                <p>
                  Sharing depends on your <strong>Group → Team → User</strong> hierarchy, which
                  Admins manage in Settings → Teams.
                </p>
              </SubSection>

              {/* Initiative Details */}
              <Section id="initiatives" title="Initiative Details" icon={DocumentTextIcon}>
                <p>
                  Clicking any initiative opens a detail panel with multiple tabs. Viewers see a
                  read-only panel; Editors and Admins see edit controls for initiatives they own,
                  and Admins can edit any initiative.
                </p>
              </Section>

              <SubSection id="initiatives-main" title="Main Tab">
                <p>
                  Shows the core fields: title, product/sub-product, classification, status, start
                  and end quarter (or exact dates), owner, and tags. Which fields are shown,
                  required, and how they're labeled is configurable by Admins in Settings →
                  Initiative Fields. Editors and Admins can update any field and save changes.
                </p>
              </SubSection>

              <SubSection id="initiatives-dependencies" title="Dependencies Tab">
                <p>
                  Shows all initiatives that this initiative depends on. Search for an initiative by
                  name to add a dependency, or click the remove button next to an existing one.
                  Dependencies are managed individually — changes take effect immediately without
                  needing to save the whole form. The system prevents circular dependencies
                  automatically.
                </p>
              </SubSection>

              <SubSection id="initiatives-resources" title="Resources Tab">
                <p>
                  Optional tab for attaching reference links to an initiative. Each resource has a
                  title and a URL, listed with the title as a clickable link that opens in a new
                  tab. Editors and Admins can add, edit, and delete resources; Viewers see the list
                  in read-only mode.
                </p>
              </SubSection>

              <SubSection id="initiatives-scoring" title="Scoring Tab">
                <p>
                  Optional tab (Settings → Labs → Scoring) for prioritising initiatives. Rate two
                  dimensions on a 1–5 scale by clicking the dots: <strong>Impact</strong> (green) and{' '}
                  <strong>Effort</strong> (purple). Click the highest filled dot again to clear a
                  rating. Changes save automatically, and only the owner or an Admin can change
                  them.
                </p>
                <p>
                  Anyone can vote for an initiative using the thumbs-up button in the modal header —
                  including Viewers. Each person gets one vote per initiative; click again to remove
                  it.
                </p>
              </SubSection>

              <SubSection id="initiatives-intelligence" title="Intelligence Tab">
                <p>
                  Optional tab showing AI-generated, per-initiative insights — a short analysis of
                  risk, momentum, and open questions based on the initiative's fields, comments, and
                  history. Available when <strong>Item-level Insights</strong> is enabled in
                  Settings → Labs.
                </p>
              </SubSection>

              <SubSection id="initiatives-comments" title="Comments Tab">
                <p>
                  A threaded discussion area for each initiative. Type a comment and submit to post.
                  When @Mentions is enabled, you can @mention other users by typing{' '}
                  <code>@</code> followed by their name — select a match to insert a mention token
                  that appears as highlighted blue text for all readers. Mentioned users receive an
                  in-app notification and, if enabled, an email.
                </p>
                <p>
                  Comments are visible to all authenticated users; only Editors and Admins can post.
                </p>
              </SubSection>

              <SubSection id="initiatives-auditlog" title="Audit Log Tab">
                <p>
                  Shows a chronological history of every change made to this specific initiative —
                  field updates, status changes, dependency additions and removals, and creation.
                  This tab is read-only and available to all authenticated users.
                </p>
              </SubSection>

              <SubSection id="initiatives-sharing" title="Sharing">
                <p>
                  Every initiative has a unique shareable URL. Open its detail panel and click the{' '}
                  <strong>share icon</strong> in the header to copy the link to your clipboard. A
                  green checkmark confirms the copy. Opening the link takes a colleague straight to
                  that initiative's detail panel (after logging in, if needed).
                </p>
              </SubSection>

              <SubSection id="initiatives-starred" title="Starred">
                <p>
                  Use the <strong>star</strong> icon in the detail header to flag an initiative as
                  strategically important — it works like a shared, organization-wide bookmark, not
                  a personal one. Once starred, everyone sees the amber star on it across Timeline,
                  List, Kanban, and Calendar. Only the initiative's <strong>owner</strong> or an{' '}
                  <strong>Admin</strong> can toggle it.
                </p>
              </SubSection>

              {/* Reports */}
              <Section id="reports" title="Reports" icon={ClipboardDocumentListIcon}>
                <p>
                  Reports are accessible from the <strong>Reports</strong> dropdown in the
                  navigation bar. They provide organization-wide views across all initiatives, and
                  are shown only when the corresponding Labs feature is enabled.
                </p>
              </Section>

              <SubSection id="reports-audit" title="Audit Log">
                <p>
                  A global feed of all changes made to any initiative across the entire roadmap,
                  sorted by most recent. Entries are grouped by date and show what changed, the old
                  value, and the new value. Click any entry to open the related initiative.
                </p>
              </SubSection>

              <SubSection id="reports-comments" title="Comments Log">
                <p>
                  A centralized feed of all comments across every initiative, sorted by most recent.
                  Each comment shows the author, the initiative it belongs to, and its product
                  context. Click any comment to open the related initiative.
                </p>
              </SubSection>

              <SubSection id="reports-dependency" title="Dependency Log">
                <p>
                  A dedicated view for tracking cross-initiative dependencies. Parent initiatives
                  (those that have dependencies) appear as expandable rows, with their dependencies
                  nested underneath. Use this log to identify blockers and understand inter-team
                  dependencies across products.
                </p>
              </SubSection>

              <SubSection id="reports-scoring" title="Scoring">
                <p>
                  Optional report (Settings → Labs → Scoring) for prioritising initiatives across
                  the roadmap, with a List / Chart toggle in the header; your active filters carry
                  across both.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    <strong>List view</strong> — a sortable table showing Impact, Effort, and Votes,
                    grouped by product/sub-product. Owners and Admins can edit Impact, Effort, and
                    Status inline; anyone can vote.
                  </li>
                  <li>
                    <strong>Chart view</strong> — an Impact vs Effort bubble chart. Each initiative
                    is plotted by Effort (x-axis) and Impact (y-axis). Click a bubble to open that
                    initiative.
                  </li>
                </ul>
                <p>
                  The filter bar's Impact / Effort / Votes filters (also available inline on the
                  Scoring tab of an initiative) let you narrow either view further.
                </p>
              </SubSection>

              {/* Batch Update */}
              <Section id="batch-update" title="Batch Update" icon={PencilSquareIcon}>
                <p>
                  <strong>Batch Update</strong> is an <strong>Admin-only</strong> tool (Settings →
                  Labs, opened from the <strong>Reports</strong> menu) for changing one field across
                  many initiatives at once.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    <strong>Filter &amp; select.</strong> Use the filter bar to narrow the list,
                    then tick the initiatives you want to change (or select all).
                  </li>
                  <li>
                    <strong>Choose a field &amp; value.</strong> Pick a single field — title,
                    sub-product, classification, status, owner, start/end month, tags, starred, or
                    description — and the new value to apply.
                  </li>
                  <li>
                    <strong>Review &amp; apply.</strong> A confirmation step shows exactly what will
                    change and on how many initiatives before you confirm; the change applies in
                    chunks with a progress bar, and invalid records are skipped rather than blocking
                    the whole run.
                  </li>
                  <li>
                    <strong>Undo.</strong> Each run is recorded with the previous values, so a batch
                    change can be <strong>rolled back</strong> — either mid-run ("Stop & roll back")
                    or after the fact from the result screen.
                  </li>
                </ul>
                <p>
                  Every change still respects permissions and is written to the Audit Log, just like
                  editing initiatives one at a time.
                </p>
              </Section>

              {/* Intelligence with AI */}
              <Section id="intelligence" title="Intelligence with AI" icon={SparklesIcon}>
                <p>
                  Optional AI features (Settings → Labs → Intelligence with AI) that use either the
                  built-in AI provider or your own API key (configured in Settings → AI Provider).
                  Each sub-feature can be toggled independently:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    <strong>Create with AI</strong> — the amber "Create with AI" button in the top
                    nav opens a dialog where you describe an initiative in plain English and the AI
                    drafts the fields for you to review before saving.
                  </li>
                  <li>
                    <strong>Item-level Insights</strong> — a per-initiative AI analysis available in
                    the initiative's <strong>Intelligence</strong> tab.
                  </li>
                  <li>
                    <strong>App-level Intelligence</strong> — the <strong>Intelligence</strong> page
                    in the top nav, with three tools: <strong>Roadmap Health</strong> (an AI
                    assessment of the overall roadmap), <strong>Ask Your Roadmap</strong> (a chat
                    interface for questions about your initiatives), and{' '}
                    <strong>Digest</strong> (a generated summary in Chat, Executive, or Team format
                    over a configurable lookback window).
                  </li>
                </ul>
                <p>
                  AI usage is metered — see <strong>Credits &amp; Referrals</strong> below for how
                  free and paid AI credits work.
                </p>
              </Section>

              {/* Credits & Referrals */}
              <Section id="billing" title="Credits & Referrals" icon={CurrencyDollarIcon}>
                <p>
                  Your organization is on the <strong>Free plan</strong>. AI features like{' '}
                  <strong>Create with AI</strong> draw from a monthly free allowance; once that's
                  used up (and you're not using your own AI provider key), further AI actions draw
                  from paid AI credits purchased through Stripe.
                </p>
                <p>
                  <strong>Refer &amp; Earn AI Credits</strong> (in the user menu) gives you a
                  referral link — you earn AI credits when a new organization signs up through it,
                  and they get a starting bonus too.
                </p>
              </Section>

              {/* Settings */}
              <Section id="settings" title="Settings" icon={Cog6ToothIcon}>
                <p>
                  Settings allow Admins to configure the building blocks of your roadmap. All
                  settings are accessible from the Settings page in the navigation bar (Admin only).
                </p>
              </Section>

              <SubSection id="settings-organization" title="Organization">
                <p>
                  Shows your organization's name, workspace URL, plan, and usage limits, plus your
                  referral link. Admins can restrict sign-in to specific email domains under{' '}
                  <strong>Allowed Email Domains</strong> — leave it empty to allow any domain.
                </p>
              </SubSection>

              <SubSection id="settings-hierarchy" title="Hierarchy">
                <p>
                  Manages both the labels and the structure of your roadmap's three levels. By
                  default they're labeled <strong>Product</strong> → <strong>Sub-Product</strong> →{' '}
                  <strong>Initiative</strong>, but Admins can rename them to match your organization
                  (e.g. "Division / Team / Epic"). Changes apply immediately across navigation,
                  column headers, filter labels, and reports.
                </p>
                <p>
                  This is also where Admins create and reorder the actual{' '}
                  <strong>Products</strong> and <strong>Sub-Products</strong> that initiatives are
                  grouped under.
                </p>
              </SubSection>

              <SubSection id="settings-fields" title="Initiative Fields">
                <p>
                  Controls which fields appear on the initiative form, their display label, and
                  whether they're required. A locked field (shown with a small padlock) can't be
                  hidden or made optional — it's a core field the app depends on.
                </p>
              </SubSection>

              <SubSection id="settings-classifications" title="Classifications">
                <p>
                  Classifications are key flags that categorize the nature of an initiative. Common
                  examples include "New Feature", "BAU" (business as usual), and "Infrastructure".
                  Each classification has a color that appears as a visual indicator on initiative
                  cards across every view.
                </p>
              </SubSection>

              <SubSection id="settings-status" title="Status">
                <p>
                  Statuses represent the progress stage of an initiative. The default set includes
                  "Discovery", "In Progress", "Blocked", and "Shipped", but these can be customized.
                  Each status has a color and appears as a dot indicator on initiative cards, and
                  maps to a column on the Kanban board when Kanban is enabled.
                </p>
              </SubSection>

              <SubSection id="settings-tags" title="Tags">
                <p>
                  Tags provide a flexible way to highlight attributes that cut across products and
                  classifications. Examples include "3rd Party Vendor", "Funding Required", or
                  "Security Review". Multiple tags can be assigned to a single initiative, and each
                  can have an icon for quick visual identification.
                </p>
              </SubSection>

              <SubSection id="settings-users" title="Users & Roles">
                <p>
                  Users are managed by Admins and invited by email — new users confirm their account
                  via an email link, then sign in with a one-time code (no passwords). Each user has
                  one of three roles that control what they can see and do:
                </p>
                <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Action</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-gray-700">Viewer</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-gray-700">Editor</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-gray-700">Admin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        ['View all data', true, true, true],
                        ['Add comments & @mentions', false, true, true],
                        ['Vote on initiatives', true, true, true],
                        ['Create initiatives', false, true, true],
                        ['Edit own initiatives', false, true, true],
                        ['Edit any initiative', false, false, true],
                        ['Delete own initiatives', false, true, true],
                        ['Delete any initiative', false, false, true],
                        ['Manage dependencies', false, true, true],
                        ['Star/unstar (own initiatives)', false, true, true],
                        ['Create & share Saved Views', false, true, true],
                        ['Run Batch Update', false, false, true],
                        ['Manage users & teams', false, false, true],
                        ['Access settings', false, false, true],
                        ['Configure hierarchy & field labels', false, false, true],
                        ['Toggle Labs features', false, false, true],
                      ].map(([action, viewer, editor, admin]) => (
                        <tr key={action as string} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-700">{action as string}</td>
                          <td className="px-4 py-2 text-center">
                            {viewer ? (
                              <span className="text-green-600 font-medium">&#10003;</span>
                            ) : (
                              <span className="text-gray-300">&mdash;</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {editor ? (
                              <span className="text-green-600 font-medium">&#10003;</span>
                            ) : (
                              <span className="text-gray-300">&mdash;</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {admin ? (
                              <span className="text-green-600 font-medium">&#10003;</span>
                            ) : (
                              <span className="text-gray-300">&mdash;</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SubSection>

              <SubSection id="settings-teams" title="Teams & Groups">
                <p>
                  Admins organize users into a <strong>Group → Team → User</strong> hierarchy — each
                  user belongs to a team, and each team belongs to a group. Create Groups and Teams
                  here, assign each team to a group, then assign each user to a team.
                </p>
                <p>
                  This hierarchy powers the sharing options in <strong>Saved Views</strong>, so a
                  view can be shared with a whole group, one or more teams, or specific people.
                </p>
              </SubSection>

              <SubSection id="settings-timeframes" title="Timeframes">
                <p>
                  Timeframes define the time boundaries and periods used across the app — the
                  timeline axis in views, and the start/end options when creating initiatives. Unlike
                  a rigid fiscal-year system, each timeframe can use <strong>Quarterly</strong> (4
                  periods), <strong>Monthly</strong> (12 periods), or fully{' '}
                  <strong>custom</strong> period labels (e.g. sprints), so the app fits how your
                  organization actually plans.
                </p>
              </SubSection>

              <SubSection id="settings-labs" title="Labs">
                <p>
                  Labs contains optional features that Admins can turn on or off. Disabling a
                  feature hides it from everyone in your organization — no data or configuration is
                  lost, so re-enabling it restores it immediately. Features are grouped into
                  modules; the <strong>Initiatives</strong> and <strong>Dashboard</strong> modules
                  are always present, but the individual features inside them (Dependencies,
                  Comments, @Mentions, Notifications, Audit Log, Charts, Recent Activity, Recent
                  Comments) can each be toggled.
                </p>
                <p>Other optional features available here:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Kanban View</strong> and <strong>Calendar View</strong> — additional roadmap views.</li>
                  <li><strong>Batch Update</strong> — Admin-only bulk field edits with rollback.</li>
                  <li><strong>Scoring</strong> — Impact/Effort ratings, voting, and the prioritisation views.</li>
                  <li><strong>Saved Views</strong> — named, shareable filter views with per-person favorites.</li>
                  <li><strong>Intelligence with AI</strong> — Create with AI, Item-level Insights, and App-level Intelligence (Roadmap Health / Ask / Digest), each toggleable individually.</li>
                  <li>
                    <strong>Jira</strong> — push individual initiatives to Jira Cloud as Epics,
                    Stories, or Tasks. Enabling it reveals an inline connection form right here in
                    Labs (instance URL, service account email, API token, with a "Test Connection"
                    button) — once connected, a "Push to Jira" action appears in the initiative
                    detail modal for Editors and Admins.
                  </li>
                </ul>
              </SubSection>

              <SubSection id="settings-ai-provider" title="AI Provider">
                <p>
                  Controls which AI model powers Intelligence features. By default the app uses the{' '}
                  <strong>built-in</strong> provider (shared, metered by AI credits). Admins can
                  switch to <strong>custom</strong> mode and supply their own API key (Anthropic,
                  OpenAI, Gemini, or OpenRouter) so AI usage bypasses the credit system entirely and
                  bills directly to that provider account.
                </p>
              </SubSection>

              <SubSection id="settings-api-keys" title="API Keys">
                <p>
                  Admin-only page for generating API keys used by external integrations (for example
                  Zapier, Make, or a custom script) to read and write initiatives through the
                  public REST API. Each key is shown in full only once, at creation — store it
                  somewhere safe. Keys can be revoked at any time.
                </p>
              </SubSection>

              {/* Bottom spacer */}
              <div className="h-16" />
            </div>
          </div>
        </div>
      </main>

      <InstanceFooter />
    </div>
  );
}
